// CHUNK 8: Public invite landing page at /sinastria/convite/:token
// Invitee fills their birth data; complete-synastry-invite generates the analysis.
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface InviteInfo {
  personAName:   string;
  synastryType:  string;
}

const TYPE_LABEL: Record<string, string> = {
  romantic:     '💕 Romântica',
  friendship:   '🤝 Amizade',
  professional: '💼 Profissional',
  family:       '✨ Familiar',
};

export function SynastryInviteLanding() {
  const { token }          = useParams<{ token: string }>();
  const navigate           = useNavigate();
  const [info,     setInfo]     = useState<InviteInfo | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [invalid,  setInvalid]  = useState(false);
  const [sending,  setSending]  = useState(false);
  const [error,    setError]    = useState('');
  const [form, setForm] = useState({
    fullName:  '',
    birthDate: '',
    birthTime: '',
    birthCity: '',
  });

  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return; }

    supabase
      .from('synastry_readings')
      .select('metadata, relation_type')
      .eq('invite_link_token', token)
      .eq('status', 'pending')
      .maybeSingle()
      .then(({ data, error: e }) => {
        if (e || !data) { setInvalid(true); }
        else {
          const meta = (data as Record<string, Record<string, string>>).metadata ?? {};
          setInfo({
            personAName:  meta.person_a_name ?? 'alguém especial',
            synastryType: (data as Record<string, string>).relation_type ?? 'romantic',
          });
        }
        setLoading(false);
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.fullName || !form.birthDate) return;
    setSending(true);
    setError('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke(
        'complete-synastry-invite',
        { body: { inviteToken: token, ...form } },
      );
      if (fnErr || !data?.synastryId) throw fnErr ?? new Error('Erro ao completar sinastria');
      navigate(`/sinastria/${data.synastryId as string}`);
    } catch {
      setError('Não foi possível enviar seus dados. Tente novamente.');
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1130] flex items-center justify-center">
        <span className="text-[#C9A84C] animate-pulse">✦</span>
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-[#0D1130] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-[#C9A84C] text-3xl mb-4">✦</div>
          <p className="text-[#F5ECD7]/70">Convite inválido ou já utilizado.</p>
          <p className="text-[#F5ECD7]/40 text-sm mt-2">Peça um novo link à pessoa que te convidou.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1130] py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="text-[#C9A84C] text-3xl tracking-[8px] mb-4">✦</div>
          <h1 className="text-[#C9A84C] text-2xl font-light tracking-wider mb-2">
            Convite de Sinastria
          </h1>
          <p className="text-[#F5ECD7]/70 text-sm leading-relaxed">
            <strong className="text-[#F5ECD7]">{info?.personAName}</strong> quer descobrir
            a conexão astrológica entre vocês
          </p>
          {info && (
            <div className="inline-block mt-3 border border-[#C9A84C]/30 px-4 py-1.5 text-[#C9A84C] text-xs tracking-wider">
              {TYPE_LABEL[info.synastryType] ?? '✦'}
            </div>
          )}
        </div>

        {/* What you'll discover */}
        <div className="border border-[#C9A84C]/20 p-5 rounded-sm mb-8">
          <h2 className="text-[#F5ECD7]/80 text-xs uppercase tracking-widest mb-4">
            O que você vai descobrir
          </h2>
          <ul className="space-y-3 text-[#F5ECD7]/70 text-sm">
            {[
              'Compatibilidade astrológica personalizada entre vocês',
              'Forças naturais da conexão e áreas de crescimento',
              'Insights baseados em mais de 10 pontos astrológicos',
              'Conselhos práticos para fortalecer o relacionamento',
            ].map(item => (
              <li key={item} className="flex gap-2">
                <span className="text-[#C9A84C] flex-shrink-0">✦</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form */}
        {error && (
          <div className="mb-4 p-3 border border-red-500/40 bg-red-900/20 text-red-300 text-xs rounded-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {([
            { key: 'fullName',  label: 'Seu nome',              type: 'text', placeholder: 'Como você gosta de ser chamada(o)', required: true },
            { key: 'birthDate', label: 'Data de nascimento',    type: 'date', placeholder: '',                                   required: true },
            { key: 'birthTime', label: 'Hora de nascimento',    type: 'time', placeholder: 'Aproximada está ótimo',               required: false },
            { key: 'birthCity', label: 'Cidade de nascimento',  type: 'text', placeholder: 'Cidade, Estado',                     required: false },
          ] as const).map(f => (
            <label key={f.key} className="block">
              <span className="block text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-2">{f.label}</span>
              <input
                type={f.type}
                value={form[f.key]}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                required={f.required}
                className="w-full bg-transparent border border-[#C9A84C]/30 text-[#F5ECD7] p-3 rounded-sm placeholder-[#F5ECD7]/30 focus:border-[#C9A84C] focus:outline-none"
              />
            </label>
          ))}

          <button
            type="submit"
            disabled={sending}
            className="mt-4 w-full py-4 bg-[#C9A84C] text-[#0D1130] text-xs uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 disabled:opacity-60 transition-colors"
          >
            {sending ? 'Gerando sua análise…' : '✦ Ver nossa sinastria ✦'}
          </button>

          <p className="text-center text-[#F5ECD7]/30 text-xs mt-3">
            Seus dados são usados apenas para gerar esta análise.
            Ao continuar, ambos verão o resultado.
          </p>
        </form>
      </div>
    </div>
  );
}
